import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isManager, ACTIVITIES } from "@/lib/constants";
import { formatDate, trainingTone } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, ArrowLeft, Save, FolderOpen, UserPlus, Power } from "lucide-react";
import { toast } from "sonner";
import FirmDocuments from "@/components/FirmDocuments";
import FirmSubstances from "@/components/FirmSubstances";
import VisitReports from "@/components/VisitReports";
import PersonnelDetail from "@/components/PersonnelDetail";
import FirmTrainingTab from "@/components/FirmTrainingTab";
import BulkCertUpload from "@/components/BulkCertUpload";
import AddPersonnelDialog from "@/components/AddPersonnelDialog";

function daysBadge(dateStr) {
  if (!dateStr) return { tone: "muted", text: "Tarih girilmedi" };
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, (m || 1) - 1, d || 1);
  const diff = Math.ceil((target - new Date()) / 86400000);
  if (diff < 0) return { tone: "overdue", text: "Süresi geçti" };
  if (diff <= 30) return { tone: "pending", text: `${diff} gün` };
  if (diff <= 60) return { tone: "pending", text: `${diff} gün` };
  if (diff <= 90) return { tone: "info", text: `${diff} gün` };
  return { tone: "active", text: `${diff} gün` };
}

export default function FirmDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const manager = isManager(user.role);
  const canEdit = manager || user.role === "advisor";
  const [firm, setFirm] = useState(null);
  const [folders, setFolders] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [selPersonnel, setSelPersonnel] = useState(null);
  const [bulkCert, setBulkCert] = useState(false);
  const [addPersonnel, setAddPersonnel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({});
  const [assign, setAssign] = useState({ responsible_tmgd_id: "", backup_tmgd_id: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/firms/${id}`);
      setFirm(data);
      setEdit({
        title: data.title || "", short_name: data.short_name || "", city: data.city || "",
        facility_address: data.facility_address || "", phone: data.phone || "", email: data.email || "",
        authorized_person: data.authorized_person || "", tax_office: data.tax_office || "", tax_number: data.tax_number || "",
        activities: data.activities || [],
        tmfb: data.tmfb || { number: "", issue_date: "", valid_until: "" },
        contract: data.contract || { start_date: "", end_date: "", status: "aktif" },
        annual_report_due: data.annual_report_due || "", annual_report_year: data.annual_report_year || "",
        security_valid_until: data.security_valid_until || "", security_year: data.security_year || "",
      });
      setAssign({ responsible_tmgd_id: data.responsible_tmgd_id || "", backup_tmgd_id: data.backup_tmgd_id || "" });
      const [fol, per] = await Promise.all([api.get(`/firms/${id}/folders`), api.get(`/firms/${id}/personnel`)]);
      setFolders(fol.data.folders);
      setPersonnel(per.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      navigate("/firmalar");
    }
  };
  useEffect(() => {
    load();
    if (isManager(user.role)) api.get("/advisors").then(({ data }) => setAdvisors(data));
  }, [id]); // eslint-disable-line

  const toggleAct = (code) => setEdit((f) => ({ ...f, activities: f.activities.includes(code) ? f.activities.filter((a) => a !== code) : [...f.activities, code] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...edit, annual_report_year: edit.annual_report_year === "" ? null : edit.annual_report_year, security_year: edit.security_year === "" ? null : edit.security_year };
      await api.put(`/firms/${id}`, payload);
      toast.success("Firma güncellendi.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveAssign = async () => {
    setSaving(true);
    try {
      await api.post(`/firms/${id}/assign`, {
        responsible_tmgd_id: assign.responsible_tmgd_id || null,
        backup_tmgd_id: assign.backup_tmgd_id || null,
      });
      toast.success("Danışman atamaları kaydedildi.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async () => {
    try {
      await api.post(`/firms/${id}/${firm.is_active ? "deactivate" : "activate"}`);
      toast.success(firm.is_active ? "Firma pasife alındı." : "Firma yeniden aktifleştirildi.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!firm) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/firmalar")} data-testid="firm-back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-slate-900">{firm.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge tone={firm.is_active ? "active" : "passive"}>{firm.is_active ? "Aktif" : "Pasif"}</StatusBadge>
            <span className="text-sm text-slate-500">{firm.city}</span>
          </div>
        </div>
        {manager && (
          <Button variant="outline" onClick={toggleActive} data-testid="firm-toggle-active" className={firm.is_active ? "text-rose-600 border-rose-200" : "text-emerald-600 border-emerald-200"}>
            <Power className="h-4 w-4 mr-2" /> {firm.is_active ? "Pasife Al" : "Aktifleştir"}
          </Button>
        )}
      </div>

      <Tabs defaultValue="genel">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="genel" data-testid="firm-tab-general">Genel & İletişim</TabsTrigger>
          <TabsTrigger value="atamalar" data-testid="firm-tab-assignments">Danışman Atamaları</TabsTrigger>
          <TabsTrigger value="faaliyet" data-testid="firm-card-tab-activities">ADR Faaliyetleri</TabsTrigger>
          <TabsTrigger value="maddeler" data-testid="firm-tab-substances">Tehlikeli Maddeler</TabsTrigger>
          <TabsTrigger value="belgeler" data-testid="firm-tab-folders">Belge Klasörleri</TabsTrigger>
          <TabsTrigger value="ziyaretler" data-testid="firm-tab-visits">Ziyaretler</TabsTrigger>
          <TabsTrigger value="personel" data-testid="firm-tab-personnel">Personel</TabsTrigger>
          <TabsTrigger value="egitim" data-testid="firm-tab-training">Tatbikat & Araç</TabsTrigger>
          <TabsTrigger value="tmfb" data-testid="firm-tab-tmfb">TMFB & Süreler</TabsTrigger>
        </TabsList>

        {/* Genel */}
        <TabsContent value="genel">
          <Card className="border-slate-200"><CardContent className="pt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Unvan</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} disabled={!canEdit} data-testid="firm-edit-title" /></div>
              <div className="space-y-1.5"><Label>Kısa Ad</Label><Input value={edit.short_name} onChange={(e) => setEdit({ ...edit, short_name: e.target.value })} disabled={!canEdit} /></div>
              <div className="space-y-1.5"><Label>Şehir</Label><Input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} disabled={!canEdit} /></div>
              <div className="space-y-1.5"><Label>Yetkili Kişi</Label><Input value={edit.authorized_person} onChange={(e) => setEdit({ ...edit, authorized_person: e.target.value })} disabled={!canEdit} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Tesis / Adres</Label><Input value={edit.facility_address} onChange={(e) => setEdit({ ...edit, facility_address: e.target.value })} disabled={!canEdit} /></div>
              <div className="space-y-1.5"><Label>Telefon</Label><Input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} disabled={!canEdit} /></div>
              <div className="space-y-1.5"><Label>E-posta</Label><Input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} disabled={!canEdit} /></div>
              <div className="space-y-1.5"><Label>Vergi Dairesi</Label><Input value={edit.tax_office} onChange={(e) => setEdit({ ...edit, tax_office: e.target.value })} disabled={!canEdit} /></div>
              <div className="space-y-1.5"><Label>Vergi No</Label><Input value={edit.tax_number} onChange={(e) => setEdit({ ...edit, tax_number: e.target.value })} disabled={!canEdit} /></div>
            </div>
            {canEdit && <Button onClick={save} className="bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="firm-save-general"><Save className="h-4 w-4 mr-2" /> Kaydet</Button>}
          </CardContent></Card>
        </TabsContent>

        {/* Atamalar */}
        <TabsContent value="atamalar">
          <Card className="border-slate-200"><CardContent className="pt-6 space-y-4">
            {!manager ? (
              <div className="text-sm text-slate-600 space-y-1">
                <div>Sorumlu TMGD: <strong>{firm.responsible_tmgd_name || "Atanmadı"}</strong></div>
                <div>Yedek TMGD: <strong>{firm.backup_tmgd_name || "Atanmadı"}</strong></div>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Sorumlu TMGD</Label>
                    <Select value={assign.responsible_tmgd_id || "none"} onValueChange={(v) => setAssign({ ...assign, responsible_tmgd_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="advisor-assign-responsible-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Seçilmedi —</SelectItem>
                        {advisors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Yedek TMGD</Label>
                    <Select value={assign.backup_tmgd_id || "none"} onValueChange={(v) => setAssign({ ...assign, backup_tmgd_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="advisor-assign-backup-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Seçilmedi —</SelectItem>
                        {advisors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={saveAssign} className="bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="advisor-assign-save"><UserPlus className="h-4 w-4 mr-2" /> Atamaları Kaydet</Button>
                <p className="text-xs text-slate-400">TMGD listesi boşsa, Kullanıcılar ekranından danışman hesabı ekleyin.</p>
              </>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Faaliyetler */}
        <TabsContent value="faaliyet">
          <Card className="border-slate-200"><CardContent className="pt-6 space-y-4">
            <p className="text-sm text-slate-500">Seçilen faaliyetlere göre belge klasörleri otomatik oluşur.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {ACTIVITIES.map((a) => (
                <label key={a.code} className={`flex items-center gap-2 text-sm p-3 rounded-lg border cursor-pointer ${edit.activities.includes(a.code) ? "border-sky-300 bg-sky-50" : "border-slate-200"} ${!manager && "opacity-70 pointer-events-none"}`} data-testid={`firm-detail-activity-${a.code}`}>
                  <Checkbox checked={edit.activities.includes(a.code)} onCheckedChange={() => toggleAct(a.code)} disabled={!manager} /> {a.label}
                </label>
              ))}
            </div>
            {manager && <Button onClick={save} className="bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="firm-save-activities"><Save className="h-4 w-4 mr-2" /> Faaliyetleri Kaydet</Button>}
          </CardContent></Card>
        </TabsContent>

        {/* Klasörler / Dosya Merkezi */}
        <TabsContent value="belgeler">
          <FirmDocuments firmId={id} firmTitle={firm.title} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="maddeler">
          <FirmSubstances firmId={id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="ziyaretler">
          <VisitReports firmId={id} canEdit={canEdit} />
        </TabsContent>

        {/* Personel */}
        <TabsContent value="personel">
          <Card className="border-slate-200"><CardContent className="pt-6">
            {canEdit && (
              <div className="flex justify-end gap-2 mb-3">
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => setAddPersonnel(true)} data-testid="add-personnel-btn"><UserPlus className="h-4 w-4 mr-1" /> Personel Ekle</Button>
                <Button variant="outline" size="sm" onClick={() => setBulkCert(true)} data-testid="bulk-cert-btn"><UserPlus className="h-4 w-4 mr-1" /> Toplu Sertifika Yükle</Button>
              </div>
            )}
            {personnel.length === 0 ? <p className="text-sm text-slate-500 py-4">Personel kaydı yok.</p> : (
              <div className="divide-y divide-slate-100">
                {personnel.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`firm-personnel-${p.id}`} onClick={() => setSelPersonnel(p.id)}>
                    <div><span className="font-medium text-slate-800">{p.name} {p.surname}</span><span className="text-slate-500 text-sm ml-2">{p.duty}</span>
                      {p.training_status && <StatusBadge tone={trainingTone(p.training_status)}>{p.training_status}</StatusBadge>}</div>
                    <StatusBadge tone={p.is_active ? "active" : "passive"}>{p.is_active ? "Aktif" : "Pasif"}</StatusBadge>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="egitim">
          <FirmTrainingTab firmId={id} canEdit={canEdit} />
        </TabsContent>

        {/* TMFB */}
        <TabsContent value="tmfb">
          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="border-slate-200"><CardHeader><CardTitle className="font-display text-base">TMFB</CardTitle></CardHeader><CardContent className="space-y-3">
              <div className="space-y-1.5"><Label>Numara</Label><Input value={edit.tmfb?.number || ""} onChange={(e) => setEdit({ ...edit, tmfb: { ...edit.tmfb, number: e.target.value } })} disabled={!canEdit} data-testid="firm-tmfb-number" /></div>
              <div className="space-y-1.5"><Label>Düzenleme Tarihi</Label><Input type="date" value={edit.tmfb?.issue_date || ""} onChange={(e) => setEdit({ ...edit, tmfb: { ...edit.tmfb, issue_date: e.target.value } })} disabled={!canEdit} data-testid="firm-tmfb-issue" /></div>
              <div className="space-y-1.5"><Label>Geçerlilik Tarihi</Label><Input type="date" value={edit.tmfb?.valid_until || ""} onChange={(e) => setEdit({ ...edit, tmfb: { ...edit.tmfb, valid_until: e.target.value } })} disabled={!canEdit} data-testid="firm-tmfb-valid" /></div>
              <div data-testid="tmfb-countdown"><StatusBadge tone={daysBadge(edit.tmfb?.valid_until).tone}>Kalan: {daysBadge(edit.tmfb?.valid_until).text}</StatusBadge></div>
            </CardContent></Card>
            <Card className="border-slate-200"><CardHeader><CardTitle className="font-display text-base">Sözleşme</CardTitle></CardHeader><CardContent className="space-y-3">
              <div className="space-y-1.5"><Label>Başlangıç Tarihi</Label><Input type="date" value={edit.contract?.start_date || ""} onChange={(e) => setEdit({ ...edit, contract: { ...edit.contract, start_date: e.target.value } })} disabled={!canEdit} data-testid="firm-contract-start" /></div>
              <div className="space-y-1.5"><Label>Bitiş Tarihi</Label><Input type="date" value={edit.contract?.end_date || ""} onChange={(e) => setEdit({ ...edit, contract: { ...edit.contract, end_date: e.target.value } })} disabled={!canEdit} data-testid="firm-contract-end" /></div>
              <div className="space-y-1.5">
                <Label>Durum</Label>
                <Select value={edit.contract?.status || "aktif"} onValueChange={(v) => setEdit({ ...edit, contract: { ...edit.contract, status: v } })}>
                  <SelectTrigger disabled={!canEdit} data-testid="firm-contract-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="askida">Askıda</SelectItem>
                    <SelectItem value="sonlandi">Sonlandı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div data-testid="contract-countdown"><StatusBadge tone={daysBadge(edit.contract?.end_date).tone}>Kalan: {daysBadge(edit.contract?.end_date).text}</StatusBadge></div>
            </CardContent></Card>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <Card className="border-slate-200"><CardHeader><CardTitle className="font-display text-base">Yıllık Faaliyet Raporu</CardTitle></CardHeader><CardContent className="space-y-3">
              <div className="space-y-1.5"><Label>Beklenen Yıl</Label><Input type="number" value={edit.annual_report_year || ""} onChange={(e) => setEdit({ ...edit, annual_report_year: e.target.value })} disabled={!canEdit} data-testid="firm-annual-year" placeholder="2027" /></div>
              <div className="space-y-1.5"><Label>Sonraki Yükleme Son Tarihi</Label><Input type="date" value={edit.annual_report_due || ""} onChange={(e) => setEdit({ ...edit, annual_report_due: e.target.value })} disabled={!canEdit} data-testid="firm-annual-due" /></div>
              <StatusBadge tone={daysBadge(edit.annual_report_due).tone}>Kalan: {daysBadge(edit.annual_report_due).text}</StatusBadge>
            </CardContent></Card>
            <Card className="border-slate-200"><CardHeader><CardTitle className="font-display text-base">Güvenlik Planı</CardTitle></CardHeader><CardContent className="space-y-3">
              <div className="space-y-1.5"><Label>Beklenen Yıl</Label><Input type="number" value={edit.security_year || ""} onChange={(e) => setEdit({ ...edit, security_year: e.target.value })} disabled={!canEdit} data-testid="firm-security-year" placeholder="2027" /></div>
              <div className="space-y-1.5"><Label>Geçerlilik / Son Yenileme Tarihi</Label><Input type="date" value={edit.security_valid_until || ""} onChange={(e) => setEdit({ ...edit, security_valid_until: e.target.value })} disabled={!canEdit} data-testid="firm-security-valid" /></div>
              <StatusBadge tone={daysBadge(edit.security_valid_until).tone}>Kalan: {daysBadge(edit.security_valid_until).text}</StatusBadge>
            </CardContent></Card>
          </div>
          {canEdit && <Button onClick={save} className="mt-4 bg-sky-600 hover:bg-sky-700" disabled={saving} data-testid="firm-save-tmfb"><Save className="h-4 w-4 mr-2" /> TMFB & Süreleri Kaydet</Button>}
          <p className="text-xs text-slate-400 mt-3">Tarih yoksa "Tarih girilmedi" gösterilir; 90/60/30 gün eşiklerinde renkli uyarı verilir. Hatırlatmalar sorumlu danışmana ve yöneticiye gider.</p>
        </TabsContent>
      </Tabs>
      {selPersonnel && <PersonnelDetail personnelId={selPersonnel} canEdit={canEdit} onClose={() => setSelPersonnel(null)} onChanged={() => { api.get(`/firms/${id}/personnel`).then(({ data }) => setPersonnel(data)); }} />}
      {bulkCert && <BulkCertUpload firmId={id} personnel={personnel} onClose={() => setBulkCert(false)} onDone={() => { api.get(`/firms/${id}/personnel`).then(({ data }) => setPersonnel(data)); }} />}
      {addPersonnel && <AddPersonnelDialog firmId={id} onClose={() => setAddPersonnel(false)} onDone={() => { api.get(`/firms/${id}/personnel`).then(({ data }) => setPersonnel(data)); }} />}
    </div>
  );
}
